<template>
<b-container>
  <b-row>
      <b-col>

      <big-panel
        title="Optimum Load Shape Signal Viewer"
        :footer="footer"
        >
        <div class="sub-title">
          {{tagline}}
        </div>
        <div class="mt-3">
          Signal:
          <b-form-select
            v-model="selectedLmp"
            :value="selectedLmp"
            :options="lmps"
            @change="fetch()">
          </b-form-select>
        </div>

        <!-- main display: chart -->
        <div class="mt-2" v-if="dataLoaded && !error">
          <div class="mt-4 mb-4">
            <b-row>
              <b-col>
                Data for: <strong>{{marketDate}}</strong>
                <span v-if="historyEnabled">
                <b-form-datepicker
                  class="ml-2"
                  id="datepicker"
                  :value="datePicked"
                  v-model="datePicked"
                  right
                  button-only
                  size="sm"
                  :max = "maxDate"
                  button-variant="light"
                  @input="selectDate">
                </b-form-datepicker>
              </span>
              </b-col>
              <b-col class="text-right">
                <b-button @click="download" sm variant="light"><b-icon-download class="pr-4"/>Download as CSV </b-button>
              </b-col>
            </b-row>
          </div>
          <div v-if="chartType==='line'">
            <line-chart :chart-data=data :options=options></line-chart>
          </div>
          <div v-else class="parent">
            <bar-chart :chart-data=data :options=options></bar-chart>
            <div class="chart-copyright">
              &copy; Robert Cruickshank Associates. All Rights Reserved.
            </div>
          </div>
        </div>
        <div v-else-if="dateInProgress && !error">
          <div class="text-center">
            <b-icon-arrow-clockwise variant="secondary" animation="spin" font-scale="3"></b-icon-arrow-clockwise>
          </div>
        </div>

        <!-- error message -->
        <div class="m-3 error" v-if="error">
          <b-row>
            <b-col cols="1" offset="1">
              <p class="h2">
                <b-icon-exclamation-diamond-fill variant="danger"/>
              </p>
            </b-col>
            <b-col class="text-center">
              <div v-if="errorType === 1">
                <p>
                  Data for the selected signal is not available. Either it is not a valid
                  signal name or there are technical issues retrieving data from the provider.
                </p>
              </div>
              <div v-if="errorType === 2">
                <p>
                  The OLS service is not currently available. It should be back momentarily.
                </p>
              </div>
              <div v-if="errorType === 3">
                <p>
                  Data for <strong>{{datePicked}}</strong> is not available. It is likely historical data for the provider does not go back that far.
                </p>
                <div>
                  Please choose a new date:
                    <b-form-datepicker
                      class="ml-2"
                      id="datepicker"
                      :value="datePicked"
                      v-model="datePicked"
                      right
                      size="sm"
                      :max = "maxDate"
                      button-variant="light"
                      @input="selectDate">
                    </b-form-datepicker>
                </div>
              </div>
              <div v-else class=" m-2">
                <b-button size="sm" variant="secondary" @click="clearError()">
                  Try again
                </b-button>
              </div>
            </b-col>
          </b-row>
        </div>

        <!-- buttons and modals -->
        <div class="mt-2">
          <b-row>
            <b-col >
              <div class="mt-3 pl-5 footer" v-if="dataLoaded && !error">
                Times are for ISO's local time zone.
              </div>
            </b-col>
            <b-col>
              <div class="text-right">
                <b-button class="mr-3" v-b-modal.modal-1 small variant="primary">Map</b-button>
                <b-button v-b-modal.modal-2 small variant="primary">Nodes</b-button>
              </div>
            </b-col>
          </b-row>
          <iso-map-modal/>
          <nodes-modal :baseUrl="baseUrl"/>
        </div>

      </big-panel>
    </b-col>
  </b-row>
  <b-row class="footer">
    <b-col>
      v{{version}}
    </b-col>
    <b-col>
      <div class="text-right">
        {{dataTimestamp}}
      </div>
    </b-col>
  </b-row>
</b-container>
</template>
<script>
import { mapGetters } from 'vuex'
import { EventBus } from '@/eventBus'
import { BIconDownload, BIconExclamationDiamondFill, BIconArrowClockwise } from 'bootstrap-vue'
import LineChart from '@/components/LineChart'
import BarChart from '@/components/BarChart'
import BigPanel from '@/components/BigPanel'
import IsoMapModal from '@/components/IsoMapModal'
import NodesModal from '@/components/NodesModal'
import axios from 'axios'

const SELECT_PLACEHOLDER = 'Click here to select an ISO'

export default {
  components: {
    BIconDownload, BIconExclamationDiamondFill, BIconArrowClockwise, BigPanel, LineChart, BarChart, IsoMapModal, NodesModal
  },
  data () {
    return {
      historyEnabled: true,
      baserUrl: '',
      dataTimestamp: '',
      marketDate: '',
      datePicked: '',
      maxDate: '',
      dataLoaded: false,
      error: false,
      errorType: 0,
      chartType: 'bar',
      dateInProgress: false,
      shadowSelection: '',
      tagline: '',
      footer: `<div class="small-title">
      Read more at 
      <a href="http://www.optimumloadshape.com"><strong>Optimumloadshape.com</strong></a>
              </div>`,
      lmps: [
        { text: SELECT_PLACEHOLDER, value: null },
        { text: 'CAISO Average', value: 'caiso%20average' },
        { text: 'ERCOT Average', value: 'ercot%20average' },
        { text: 'ISO-NE Average', value: 'iso-ne%20average' },
        { text: 'MISO Average', value: 'miso%20average' },
        { text: 'NYISO Average', value: 'nyiso%20average' },
        { text: 'PJM Average', value: 'pjm%20average' },
        { text: 'SPP Average', value: 'spp%20average' },
        { text: 'ENTSO-E Average', value: 'entsoe%20average' }
      ],
      selectedLmp: null,
      data: {
        labels: [
          '00:00', '01:00', '02:00', '03:00', '04:00', '05:00',
          '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
          '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
          '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'
        ],
        datasets: [
          {
            yAxisID: 'y',
            data: [],
            backgroundColor: '#6e8caf33', // secondary theme color
            borderColor: '#445e7c', // primary theme color
            borderWidth: 1
          },
          {
            yAxisID: 'y1',
            data: [],
            stepped: true,
            backgroundColor: '#CD5C5C33', // indian red
            borderColor: '#B22222', // fire brick
            borderWidth: 1
          }
        ]
      },
      options: {
        legend: {
          display: false
        },
        responsive: true,
        interaction: {
          mode: 'index',
          intersect: false
        },
        maintainAspectRatio: false,
        stacked: false,
        scales: {
          yAxes: [
            {
              id: 'y',
              type: 'linear',
              display: true,
              position: 'right',
              scaleLabel: {
                display: true,
                fontStyle: 'bold',
                fontSize: 14,
                fontColor: '#445e7cdd',
                labelString: 'Forecast Hourly Energy Prices ($/MWh)'
              }
            },
            {
              id: 'y1',
              type: 'linear',
              display: true,
              position: 'left',
              gridLines: {
                display: false
              },
              scaleLabel: {
                display: true,
                fontStyle: 'bold',
                fontSize: 14,
                fontColor: '#B22222',
                labelString: 'Available Energy for Each Hour (% of Total)'
              }
            }
          ]
        },
        tooltips: {
          displayColors: false
        }
      }
    }
  },
  computed: {
    ...mapGetters(['version'])
  },
  created () {
    const local = 'http://localhost:5000/ols/signals/'
    const dev = 'http://olssignalserverdev-env.eba-mnjzjmwb.us-east-1.elasticbeanstalk.com/ols/signals/'
    const prd = 'http://olssignalserver-env.eba-29hyv3uf.us-east-1.elasticbeanstalk.com/ols/signals/'
    // console.log('host: ' + document.location.host)
    if (document.location.host.startsWith('localhost')) {
      this.baseUrl = local
    } else if (document.location.host.startsWith('olsviewerdev.s3-website')) {
      this.baseUrl = dev
    } else if (document.location.host.startsWith('olsviewer.s3-website')) {
      this.baseUrl = prd
    } else {
      // console.log('problem setting baseUrl, unexpected document host')
      this.baseUrl = local
    }
    // console.log('baseUrl: ' + this.baseUrl)
    EventBus.$on('signal-selected', n => {
      const item = { text: n, value: n }
      if (!this.lmps.includes(item)) {
        this.lmps.push(item)
      }
      this.selectedLmp = n
      this.fetch()
    })
    var t = new Date()
    t = new Date(t.getTime() + 24 * 60 * 60 * 1000) // tomorrow
    this.maxDate = t.toISOString().substring(0, 10)
    var self = this
    axios.get('tagline.txt').then(
      function (response) {
        self.tagline = response.data
      },
      function (err) {
        if (err) {
          // console.log('unable to get footer ' + err)
        }
      })
  },
  methods: {
    fetch (date) {
      if (!this.selectedLmp) {
        return
      }
      if (this.lmps[0].text === SELECT_PLACEHOLDER) {
        this.lmps.splice(0, 1)
      }
      this.marketDate = ''
      this.dataTimestamp = ''
      this.dataLoaded = false
      this.data.datasets[0].data.length = 0
      this.data.datasets[1].data.length = 0
      this.error = false
      this.errorType = 0
      var url = this.baseUrl + this.selectedLmp + '?include_base_values=true'
      this.dateInProgress = false
      if (date) {
        this.dateInProgress = true
        url += '&date=' + date
      }
      // console.log('fetching: ' + url)
      var self = this
      axios.get(url).then(function (response) {
        self.dataTimestamp = 'Data retrieved: ' + new Date().toString().substring(0, 24)
        self.dataLoaded = true
        self.date = null

        if (response.data.signal === null) {
          // console.log('error - no data')
          self.error = true
          self.errorType = self.dateInProgress ? 3 : 1
        } else {
          var d = response.data.signal[0].timestamp
          self.marketDate = d.substring(5, 7) + '/' + d.substring(8, 10) + '/' + d.substring(0, 4)
          self.datePicked = d.substring(0, 4) + '-' + d.substring(4, 6) + '-' + d.substring(6, 8)
          if (response.data.signal) {
            // dataset[0] is the price - the base values
            if (response.data.iso_name !== 'PJM') {
              for (var i = 0; i < response.data.base_values.length; i++) {
                self.data.datasets[0].data.push(response.data.base_values[i])
              }
            }
            // dataset[1] is the signal
            for (i = 0; i < response.data.signal.length; i++) {
              self.data.datasets[1].data.push(response.data.signal[i].value)
            }
            self.options.scales.yAxes[0].scaleLabel.labelString = 'Forecast Hourly Energy Prices (' + response.data.currency + '/MWh)'
          }
        }
      }, function (err) {
        self.error = true
        if (err) {
          // console.log('nope ' + err)
        }
        self.errorType = 2
      })
    },
    download () {
      // console.log('download clicked')
      if (!this.dataLoaded || this.error) return
      let displayName = ''
      for (var i = 0; i < this.lmps.length; i++) {
        if (this.lmps[i].value === this.selectedLmp) {
          displayName = this.lmps[i].text
          break
        }
      }
      let contents = 'OLS Signal:, ' + displayName + '\n'
      contents += 'Market Date:, ' + this.marketDate + '\n'
      contents += 'Begin Time, $/MWh, % Availability\n'
      for (i = 0; i < this.data.datasets[0].data.length; i++) {
        contents += this.data.labels[i] + ',' + this.data.datasets[0].data[i] + ',' + this.data.datasets[1].data[i] + '\n'
      }
      const link = document.createElement('a')
      link.setAttribute('href', `data:text/csv;charset=utf-8,${encodeURIComponent(contents)}`)
      link.setAttribute('download', 'ols-export.csv')

      link.style.display = 'none'
      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
    },
    selectDate () {
      // console.log('date selected: ' + this.datePicked)
      if (this.datePicked === this.maxDate) {
        this.fetch()
      } else {
        this.fetch(this.datePicked.split('-').join(''))
      }
    },
    clearError () {
      if (this.errorType === 3) {
        this.error = false
      } else {
        this.fetch()
      }
    }
  }
}
</script>
